import React, { Fragment, useEffect, useState } from "react";
import { GetProducts, getProductsByCategory } from "../../../server/Product";
import { useSelector, useDispatch } from "react-redux";
import { NavLink, Outlet, useParams } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import ProductEditForm from "./ProductEditForm";
import Logistica from "./logistica";
import { getProductHeaderMetaParts } from "../../../utils/formatProductPackSize";

const ProductEdit = ({
  setPendingScrollSlug,
  productId: productIdProp,
  isModal = false,
  onClose,
  onProductSaved,
}) => {
  const params = useParams();
  const id = isModal ? productIdProp : productIdProp || params.id;
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname || "";
  const dispatch = useDispatch();
  const [returnTo, setReturnTo] = useState(() => location.state?.from || null);
  const [activeTab, setActiveTab] = useState("information");

  const product = useSelector((state) => state.selectedProduct.product);
  const products = useSelector((state) => state.selectedProduct.products);
  const role = useSelector((state) => state.user.role);
  const stationId = useSelector((state) => state.user.stationId);
  const categories = useSelector(
    (state) => state.selectedProduct.productCategories,
  );

  // Modal: wait until Redux product matches the opened card.
  // Route page: keep showing selected product (may load after mount).
  const resolvedProduct =
    product && (!isModal || (id && String(product.productId) === String(id)))
      ? product
      : null;

  useEffect(() => {
    // Sticky: tab NavLinks drop location.state, so keep the first known return path.
    if (location.state?.from) {
      setReturnTo(location.state.from);
    }
  }, [location.state?.from]);

  useEffect(() => {
    if (role) {
      if (!stationId || !id) return;

      dispatch(getProductsByCategory(stationId, id));
    }
  }, [role, id, stationId, dispatch]);

  useEffect(() => {
    if (!role) return;
    if (!products?.length) {
      dispatch(GetProducts());
    }
  }, [role, products?.length, dispatch]);

  useEffect(() => {
    if (!role || isModal) return;
    setTimeout(() => window.scrollTo(0, 0), 200);
  }, [role, id, isModal]);

  useEffect(() => {
    if (isModal) setActiveTab("information");
  }, [isModal, id]);

  const handleBack = () => {
    if (isModal) {
      onClose?.();
      return;
    }

    const productId = resolvedProduct?.productId || id;

    if (returnTo) {
      navigate(returnTo, {
        state: { scrollToProductId: productId },
      });
      return;
    }

    const hierarchy = categories?.[0]?.categoryHierarchy;
    if (!Array.isArray(hierarchy) || hierarchy.length === 0) {
      navigate(-1);
      return;
    }

    const reversedNav = [...hierarchy].reverse();
    if (reversedNav.length > 0) {
      const firstCategory = reversedNav[0];
      navigate(`/category/${firstCategory.slug}`, {
        state: { scrollToProductId: productId },
      });
    }
    if (reversedNav.length > 1) {
      const secondCategory = reversedNav.at(-1);
      setPendingScrollSlug(secondCategory.slug);
    }
  };

  const headerParts = getProductHeaderMetaParts(resolvedProduct, {
    includeGtin: true,
  });
  const headerMetaParts = getProductHeaderMetaParts(resolvedProduct);

  if (!resolvedProduct) {
    if (!isModal) return null;
    return (
      <div className="product-edit product-edit--modal">
        <header className="product-edit-modal-header">
          <h2 className="product-edit-modal-header__title">הגדרות מוצר</h2>
        </header>
        <div className="product-edit-modal-scroll">
          <p className="product-edit-modal-loading">טוען הגדרות מוצר…</p>
        </div>
      </div>
    );
  }

  const tabs = (
    <div className="product-edit-tabs" role="tablist" aria-label="הגדרות מוצר">
      {isModal ? (
        <>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "information"}
            className={activeTab === "information" ? "tab active" : "tab"}
            onClick={() => setActiveTab("information")}
          >
            <span>פרטי מוצר</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "logistic"}
            className={activeTab === "logistic" ? "tab active" : "tab"}
            onClick={() => setActiveTab("logistic")}
          >
            <span>לוגיסטיקה</span>
          </button>
        </>
      ) : (
        <>
          <NavLink to="information" className="tab">
            <span>פרטי מוצר</span>
          </NavLink>
          <NavLink to="logistic" className="tab">
            <span>לוגיסטיקה</span>
          </NavLink>
          <NavLink to="img" className="tab">
            <span>תמונות</span>
          </NavLink>
          <NavLink to="comper" className="tab">
            <span>מחירים</span>
          </NavLink>
          <NavLink to="inventory" className="tab">
            <span>העברת מלאי</span>
          </NavLink>
        </>
      )}
    </div>
  );

  if (isModal) {
    const handleSaved = (productsToChange, payload) => {
      onProductSaved?.(productsToChange, payload);
      onClose?.();
    };

    return (
      <div className="product-edit product-edit--modal">
        <header className="product-edit-modal-header">
          <h2 className="product-edit-modal-header__title">הגדרות מוצר</h2>
          {headerMetaParts.length ? (
            <p className="product-edit-modal-header__name">
              {headerMetaParts.join(" | ")}
            </p>
          ) : null}
          {resolvedProduct.gtin ? (
            <p className="product-edit-modal-header__gtin">
              ברקוד: {resolvedProduct.gtin}
            </p>
          ) : null}
        </header>
        {tabs}
        <div className="outlet product-edit-modal-scroll">
          <div
            className="product-edit-tab-panel"
            hidden={activeTab !== "information"}
          >
            <ProductEditForm
              setPendingScrollSlug={setPendingScrollSlug}
              onSaved={handleSaved}
            />
          </div>
          <div
            className="product-edit-tab-panel"
            hidden={activeTab !== "logistic"}
          >
            <Logistica onSaved={handleSaved} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <h2 onClick={handleBack} style={{ cursor: "pointer" }}>
        ↪ {headerParts.join(" | ")}
      </h2>

      {tabs}
      <div className="outlet">
        <div
          className="product-edit-tab-panel"
          hidden={!pathname.endsWith("/information")}
        >
          <ProductEditForm setPendingScrollSlug={setPendingScrollSlug} />
        </div>
        <div
          className="product-edit-tab-panel"
          hidden={!pathname.endsWith("/logistic")}
        >
          <Logistica />
        </div>
        {!pathname.endsWith("/information") &&
        !pathname.endsWith("/logistic") ? (
          <Outlet />
        ) : null}
      </div>
    </Fragment>
  );
};

export default ProductEdit;
